import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import GoogleMapsLoader from './GoogleMapsLoader';

interface AddressAutocompleteModalProps {
  value: string;
  onChange: (address: string) => void;
  onSelect: (address: {
    street: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
    formatted: string;
    lat?: number;
    lng?: number;
    raw_place?: google.maps.places.PlaceResult;
  }) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  onManualSave?: (address: string, lat?: number, lng?: number) => void;
}

/**
 * Address autocomplete for use inside modals.
 *
 * Uses AutocompleteService + PlacesService.getDetails instead of the built-in
 * google.maps.places.Autocomplete widget. The built-in widget appends a
 * .pac-container to <body> which breaks inside scrollable modals on iOS
 * (the dropdown detaches from the input on scroll). By fetching predictions
 * ourselves and rendering an inline dropdown, the suggestions scroll naturally
 * with the form.
 */
function AddressInputModal({
  value,
  onChange,
  onSelect,
  placeholder = "Enter an address",
  className = "",
  autoFocus = false,
  onManualSave,
}: AddressAutocompleteModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // Track the last address confirmed via dropdown selection
  const confirmedValueRef = useRef(value || '');
  const selectingRef = useRef(false);

  // Initialize AutocompleteService
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 20;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const checkAndInit = () => {
      retryCount++;
      if (window.google?.maps?.places) {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        setInitialized(true);
        console.log('[AddressAutocompleteModal] AutocompleteService initialized');
      } else if (retryCount < maxRetries) {
        timeoutId = setTimeout(checkAndInit, 500);
      } else {
        console.error('[AddressAutocompleteModal] Google Maps Places not available after retries');
      }
    };

    checkAndInit();
    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, []);

  // Fetch predictions
  const fetchPredictions = useCallback((input: string) => {
    if (!autocompleteServiceRef.current || !input || input.length < 2) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    autocompleteServiceRef.current.getPlacePredictions(
      {
        input,
        types: ['address'],
        sessionToken: sessionTokenRef.current || undefined,
      },
      (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
          setShowDropdown(true);
          setActiveIndex(-1);
        } else {
          setPredictions([]);
          setShowDropdown(false);
        }
      }
    );
  }, []);

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(newValue), 250);
  };

  // Handle prediction selection
  const handleSelectPrediction = (prediction: google.maps.places.AutocompletePrediction) => {
    const placeId = prediction.place_id;
    const description = prediction.description;

    selectingRef.current = true;

    // Close dropdown immediately
    setShowDropdown(false);
    setPredictions([]);
    onChange(description);

    // Fetch full place details
    const service = new google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails(
      {
        placeId,
        fields: ['address_components', 'formatted_address', 'geometry'],
        sessionToken: sessionTokenRef.current || undefined,
      },
      (place, status) => {
        selectingRef.current = false;
        // Generate a new session token after selection (per Google best practices)
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();

        if (status === google.maps.places.PlacesServiceStatus.OK && place?.address_components) {
          const components: { [key: string]: string } = {};
          place.address_components.forEach(component => {
            component.types.forEach(type => {
              components[type] = component.long_name;
              components[type + '_short'] = component.short_name;
            });
          });

          const streetNumber = components['street_number'] || '';
          const route = components['route'] || '';
          const city = components['locality'] || components['postal_town'] || '';
          const province = components['administrative_area_level_1_short'] || components['administrative_area_level_1'] || '';
          const postalCode = components['postal_code'] || '';
          const street = `${streetNumber} ${route}`.trim();

          const formattedParts = [street, city, province, postalCode, 'Canada'].filter(Boolean);
          const formattedAddress = formattedParts.join(', ');

          const lat = place.geometry?.location?.lat();
          const lng = place.geometry?.location?.lng();

          confirmedValueRef.current = formattedAddress;

          onChange(formattedAddress);
          onSelect({
            street,
            city,
            province,
            postalCode: postalCode || '',
            country: 'Canada',
            formatted: formattedAddress,
            lat,
            lng,
            raw_place: place,
          });

          // Update input value directly
          if (inputRef.current) {
            inputRef.current.value = formattedAddress;
          }
        }
      }
    );
  };

  // On blur: if the user typed something without selecting from the dropdown, revert
  const handleBlur = () => {
    setTimeout(() => {
      if (selectingRef.current) return;
      const currentInput = inputRef.current?.value || '';
      if (currentInput !== confirmedValueRef.current) {
        onChange(confirmedValueRef.current);
        if (inputRef.current) {
          inputRef.current.value = confirmedValueRef.current;
        }
      }
      setShowDropdown(false);
    }, 300);
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || predictions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev < predictions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : predictions.length - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelectPrediction(predictions[activeIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Close dropdown when clicking outside (include the portaled list)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (inputRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside as any);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside as any);
    };
  }, []);

  // Sync external value to input and keep confirmedValue up to date
  useEffect(() => {
    if (inputRef.current && inputRef.current.value !== value && document.activeElement !== inputRef.current) {
      inputRef.current.value = value;
    }
    // If parent sets a value (e.g. pre-populated address), treat it as confirmed
    if (value && value !== confirmedValueRef.current) {
      confirmedValueRef.current = value;
    }
  }, [value]);

  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const rafRef = useRef<number | null>(null);

  const updateDropdownPos = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, []);

  useEffect(() => {
    if (!showDropdown || predictions.length === 0) return;

    updateDropdownPos();

    const onScrollOrResize = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateDropdownPos);
    };

    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [showDropdown, predictions, updateDropdownPos]);

  const handleManualSave = useCallback(async () => {
    const address = inputRef.current?.value || value;
    if (!address?.trim() || !onManualSave) return;
    selectingRef.current = true;
    setIsGeocoding(true);
    try {
      if (window.google?.maps) {
        const geocoder = new google.maps.Geocoder();
        const result = await new Promise<google.maps.GeocoderResult | null>((resolve) => {
          geocoder.geocode({ address }, (results, status) => {
            if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
              resolve(results[0]);
            } else {
              resolve(null);
            }
          });
        });
        const lat = result?.geometry?.location?.lat();
        const lng = result?.geometry?.location?.lng();
        confirmedValueRef.current = address;
        onManualSave(address, lat, lng);
      } else {
        confirmedValueRef.current = address;
        onManualSave(address);
      }
    } catch {
      confirmedValueRef.current = address;
      onManualSave(address);
    } finally {
      setIsGeocoding(false);
    }
  }, [value, onManualSave]);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        defaultValue={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={() => {
          const current = inputRef.current?.value || value;
          if (predictions.length > 0) {
            setShowDropdown(true);
            updateDropdownPos();
          } else if (current.trim().length >= 2) {
            fetchPredictions(current);
          }
        }}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        autoFocus={autoFocus}
        style={{ paddingRight: onManualSave ? '36px' : undefined }}
      />

      <div className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${onManualSave ? '' : 'pointer-events-none'}`}>
        {onManualSave ? (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleManualSave}
            disabled={isGeocoding || !(inputRef.current?.value || value)?.trim()}
            className={`flex items-center justify-center w-6 h-6 rounded-full transition-colors ${
              isGeocoding || !(value || '').trim()
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-blue-500 hover:bg-blue-50 active:bg-blue-100 cursor-pointer'
            }`}
            title="Save address"
          >
            {isGeocoding ? (
              <div className="animate-spin rounded-full h-3.5 w-3.5 border border-blue-300 border-t-blue-600" />
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
        ) : (
          <div className="text-gray-400 text-xs" title={initialized ? "Google Maps Autocomplete Ready" : "Google Maps Not Loaded"}>
            {initialized ? '✓' : '⚠️'}
          </div>
        )}
      </div>

      {/* Dropdown rendered via portal — tracks input position on scroll */}
      {showDropdown && predictions.length > 0 && dropdownPos && createPortal(
        <div
          ref={dropdownRef}
          className="bg-white rounded-lg shadow-xl border border-gray-200 max-h-60 overflow-y-auto"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 100400,
          }}
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              className={`w-full text-left px-3 py-2.5 text-sm border-b border-gray-100 last:border-b-0 flex items-start gap-2 transition-colors ${
                index === activeIndex ? 'bg-blue-50' : 'hover:bg-gray-50 active:bg-gray-100'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectPrediction(prediction);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                handleSelectPrediction(prediction);
              }}
            >
              <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div className="min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {prediction.structured_formatting.main_text}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {prediction.structured_formatting.secondary_text}
                </div>
              </div>
            </button>
          ))}
          <div className="px-3 py-1.5 text-right">
            <span className="text-[10px] text-gray-400">powered by <span className="font-medium text-gray-500">Google</span></span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function AddressAutocompleteModal(props: AddressAutocompleteModalProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "AIzaSyCGC4-0ZxPH8WLgxMYBhVmyscFSxk0Yhx8";

  return (
    <GoogleMapsLoader apiKey={apiKey}>
      <AddressInputModal {...props} />
    </GoogleMapsLoader>
  );
}
